/**
 * Shared Formatters
 * 
 * Centralized date/time/duration/currency formatting utilities.
 * Replaces inline definitions scattered across page components.
 */

/**
 * Full month name array — shared by Usage, Calls, and any month-navigator UI.
 */
export const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Helper to ensure a date string (like SQLite CURRENT_TIMESTAMP "YYYY-MM-DD HH:MM:SS")
 * is treated as UTC rather than Local Time.
 * @param {string|Date} dateVal 
 * @returns {Date}
 */
export function parseUTCDate(dateVal) {
    if (!dateVal) return new Date();
    if (dateVal instanceof Date) return dateVal;
    
    // If it's a SQL-like timestamp "2024-03-30 19:39:00" without timezone
    if (typeof dateVal === 'string' && dateVal.includes(' ') && !dateVal.includes('T') && !dateVal.endsWith('Z')) {
        return new Date(dateVal.replace(' ', 'T') + 'Z');
    }
    return new Date(dateVal);
}

/**
 * Format a number as a currency string (e.g. "£12.50").
 * @param {number|string} value
 * @param {string} [symbol='£']
 * @param {number} [decimals=2]
 * @returns {string}
 */
export function formatCurrency(value, symbol = '£', decimals = 2) {
    const num = parseFloat(value);
    if (isNaN(num)) return `${symbol}0.${'0'.repeat(decimals)}`;
    return `${symbol}${num.toFixed(decimals)}`;
}

/**
 * Format a date string as "21 Feb 2026" (date only, no time).
 * @param {string|Date} date
 * @returns {string}
 */
export function formatDate(date) {
    if (!date) return '-';
    return parseUTCDate(date).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
    });
}

/**
 * Format a date string as "21 Feb" (short, no year).
 * @param {string|Date} date
 * @returns {string}
 */
export function formatDateShort(date) {
    if (!date) return '-';
    return parseUTCDate(date).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short'
    });
}

/**
 * Format a date string as "Monday, 21 February 2026" (full long form).
 * @param {string|Date} date
 * @returns {string}
 */
export function formatDateLong(date) {
    if (!date) return '-';
    return parseUTCDate(date).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
}

/**
 * Format a date as "February 2026" (month + year only).
 * @param {string|Date} date
 * @returns {string}
 */
export function formatMonthYear(date) {
    if (!date) return '-';
    return parseUTCDate(date).toLocaleDateString('en-GB', {
        month: 'long', year: 'numeric'
    });
}

/**
 * Format a date as "Mon, Feb 21" (compact weekday + short month).
 * @param {string|Date} date
 * @returns {string}
 */
export function formatDateCompact(date) {
    if (!date) return '-';
    return parseUTCDate(date).toLocaleDateString('en-GB', {
        weekday: 'short', month: 'short', day: 'numeric'
    });
}

/**
 * Format a date string as "21 Feb, 14:30" (date + time, no year).
 * @param {string|Date} dateStr
 * @returns {string}
 */
export function formatDateTime(dateStr) {
    if (!dateStr) return '-';
    return parseUTCDate(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
}

/**
 * Format seconds into "M:SS" duration string.
 * @param {number} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
}
